import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useGetChannelStatus } from '@/features/integrations/api';
import type { ChannelStatusItem } from '@borradh-workspace/contracts';
import type { ChannelFixAction } from '@borradh-workspace/labels';
import { AlertTriangle } from 'lucide-react';

/** Button text per fix. Claire's `fixStep` quotes these words exactly. */
const fixButtonLabels: Record<ChannelFixAction, string> = {
  connect_meta: 'Connect Facebook & Instagram',
  reconnect_meta: 'Reconnect Facebook & Instagram',
  link_instagram_to_page: 'Reconnect Facebook & Instagram',
  reconnect_instagram: 'Reconnect Instagram',
  connect_whatsapp: 'Connect WhatsApp',
  reconnect_whatsapp: 'Reconnect WhatsApp',
  contact_provider: 'Open Meta',
};

/**
 * `actionUrl` comes from a stored connection-health row, so it is not trusted
 * as an href: only https links and same-site paths are rendered. Anything else
 * (`javascript:`, `data:`, plain http, protocol-relative) gets no link.
 */
export function safeActionHref(url: string | null): string | null {
  if (!url) return null;
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  try {
    return new URL(url).protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

export interface ChannelStatusPanelProps {
  /** Starts the flow for a fix; `contact_provider` opens the Meta link instead. */
  onFix: (action: Exclude<ChannelFixAction, 'contact_provider'>) => void;
}

/**
 * Show only current connection issues. Healthy channels and channels that
 * were never set up need no extra status row. Each issue shows only its error
 * and fix button, with the action names Claire uses (ENG-1944).
 */
export function ChannelStatusPanel({ onFix }: ChannelStatusPanelProps) {
  const { channels, isLoading, isError, isRefetching, refetch } =
    useGetChannelStatus();
  const issues = channels.filter(
    (channel) => channel.status === 'needs_reconnect'
  );

  if (!isError && (isLoading || issues.length === 0)) return null;

  return (
    <div className="flex flex-col gap-3" data-testid="channel-status-panel">
      {isError ? (
        // A failed lookup says so and offers a retry; it must never render
        // as "everything is disconnected", and never as an empty card.
        <Alert variant="destructive" data-testid="channel-status-error">
          <AlertTriangle className="size-4" />
          <AlertDescription className="flex min-w-0 flex-col items-start gap-3">
            <p className="max-w-prose break-words">
              We couldn't check your connections.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="h-auto min-h-8 max-w-full whitespace-normal text-left text-foreground"
              onClick={() => void refetch()}
              disabled={isRefetching}
            >
              {isRefetching ? 'Checking…' : 'Try again'}
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        issues.map((channel) => (
          <ChannelError key={channel.channel} channel={channel} onFix={onFix} />
        ))
      )}
    </div>
  );
}

function ChannelError({
  channel,
  onFix,
}: {
  channel: ChannelStatusItem;
  onFix: ChannelStatusPanelProps['onFix'];
}) {
  const action = channel.fixAction;
  const actionHref = safeActionHref(channel.actionUrl);
  return (
    <Alert
      variant="destructive"
      data-testid={`channel-status-${channel.channel}`}
    >
      <AlertTriangle className="size-4" />
      <AlertDescription className="flex min-w-0 flex-col items-start gap-3">
        <p className="max-w-prose break-words">
          {channel.problem ?? `${channel.label} needs reconnecting.`}
        </p>
        {action === 'contact_provider' && actionHref ? (
          <Button
            asChild
            variant="outline"
            size="sm"
            className="h-auto min-h-8 max-w-full whitespace-normal text-left text-foreground"
          >
            <a href={actionHref} target="_blank" rel="noreferrer">
              {fixButtonLabels.contact_provider}
            </a>
          </Button>
        ) : action && action !== 'contact_provider' ? (
          <Button
            variant="outline"
            size="sm"
            className="h-auto min-h-8 max-w-full whitespace-normal text-left text-foreground"
            onClick={() => onFix(action)}
          >
            {fixButtonLabels[action]}
          </Button>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}
